import { getNativeIosAuthHeaders, getNativeIosLinkedUser, setNativeIosLinkedUser, type NativeIosLinkedUser } from './nativeApiRouting';
import type { IosStoreKitEntitlementResult } from './iosStoreKit';

export const APPLE_ACCOUNT_RESTORE_REQUIRED_KEY = 'zwn_account_deleted_restore_required';
let lastProof = '';
let lastSession = '';
let lastCheck = 0;
let connecting: Promise<void> | null = null;

export function appleAccountRestoreRequired(): boolean {
  return localStorage.getItem(APPLE_ACCOUNT_RESTORE_REQUIRED_KEY) === '1';
}

/** A StoreKit purchase proves paid access; this exchange also establishes the
 * server identity needed to save a personal JSA. It grants no company role. */
export async function connectApplePurchase(entitlements: IosStoreKitEntitlementResult, explicitRestore = false): Promise<void> {
  if (!entitlements.active || (appleAccountRestoreRequired() && !explicitRestore)) return;
  const transaction = [...entitlements.transactions].sort((a, b) =>
    String(b.expiresDate || b.purchaseDate).localeCompare(String(a.expiresDate || a.purchaseDate)))[0];
  const proof = transaction?.signedTransaction;
  if (!proof) throw new Error('Update the iPhone app, then use Restore Purchases to connect your personal tools.');
  const before = getNativeIosLinkedUser();
  if (proof === lastProof && before?.sessionToken === lastSession && Date.now() - lastCheck < 5 * 60_000) return;
  if (connecting) { await connecting; return connectApplePurchase(entitlements, explicitRestore); }
  connecting = (async () => {
    const response = await fetch('/api/auth/native-ios/storekit', {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...getNativeIosAuthHeaders() },
      body: JSON.stringify({ signedTransaction: proof }),
    });
    const result = await response.json();
    if (!response.ok || !result.success || !result.user?.sessionToken) {
      throw new Error(result.error || 'Could not connect your Apple purchase. Retry without purchasing again.');
    }
    if (getNativeIosLinkedUser()?.sessionToken !== before?.sessionToken) {
      throw new Error('Your account changed. Retry connecting your Apple purchase.');
    }
    const sameAccount = result.user.userId === before?.userId;
    const user: NativeIosLinkedUser = { ...result.user,
      companyId: result.user.companyId || (sameAccount ? before?.companyId : null) || null,
      companyName: (sameAccount ? before?.companyName : null) || null };
    // A later account switch must invalidate this cache even with the same receipt.
    lastProof = proof;
    lastSession = user.sessionToken || '';
    lastCheck = Date.now();
    if (explicitRestore) localStorage.removeItem(APPLE_ACCOUNT_RESTORE_REQUIRED_KEY);
    setNativeIosLinkedUser(user);
  })();
  try { await connecting; } finally { connecting = null; }
}
